import { makeRng } from "@fiveout/core";
import { overriddenCardHeight, type TransitionPlan, type TransitionStyle } from "./timing";

/**
 * Where every card in a screen transition starts, lands and leaves.
 *
 * Pure, and in PIXELS rather than percentages, for one reason: the thing worth
 * asserting about a transition is that the pack actually hides the screen at the
 * moment the route is swapped, and a rotated card's footprint cannot be tested
 * in a space where the two axes are scaled differently. `coversViewport` walks a
 * grid of points and asks whether each one is under a card; that only means
 * anything if the cards are rectangles rather than parallelograms.
 *
 * The other property being asserted is arithmetic: every card's `delay + dur`
 * equals the phase it belongs to, so nothing is still arriving after the swap
 * and nothing is still leaving when the overlay is pulled. Together those two
 * are the whole of "the player never sees the old screen become the new one".
 */

/** A card that landed face down. Not an index into the hand. */
export const FACE_DOWN = -1;

/** `pixi/cardArt`'s CARD_WIDTH / CARD_HEIGHT. Re-stated rather than imported: that module pulls in Pixi, and this one ships at the root. */
export const CARD_ASPECT = 78 / 110;
/** `pixi/cardArt`'s CORNER over each dimension, so a scaled card keeps the same printed corner. */
export const CARD_RADIUS = { x: (11 / 78) * 100, y: (11 / 110) * 100 };

export interface Viewport {
  width: number;
  height: number;
}

/** One end of a card's travel: where it is, and when it moves. */
export interface Move {
  x: number;
  y: number;
  rot: number;
  /** Extra scale at this end. Only the gust uses it; 1 everywhere else. */
  scale: number;
  delayMs: number;
  durMs: number;
  ease: string;
  /**
   * Keyframes for a move that no single easing curve can describe, from the
   * card's start (offset 0) to its end (offset 1), played linearly between.
   * Only the deal's exit has one: a pushed card moves exactly as the rail does,
   * and the rail's position is a function, not a bezier.
   */
  path?: PathPoint[];
}

/** One sampled point of a `Move.path`. `offset` is a fraction of the move's own duration. */
export interface PathPoint {
  offset: number;
  x: number;
  y: number;
  rot: number;
}

export interface TransitionCard {
  key: number;
  /**
   * Which card of the dealt hand this is: an index into `cardImages`'s `HAND`,
   * or `FACE_DOWN` for one that landed on its back.
   *
   * Decided here rather than in the overlay because it is part of what makes one
   * transition differ from the next — the hand is FIXED so it can be rendered
   * once and cached, and the shuffle is this, drawn from the run's own seed.
   */
  art: number;
  /** Landed center, in px from the overlay box's top-left. */
  x: number;
  y: number;
  width: number;
  height: number;
  /** Landed rotation, degrees. */
  rot: number;
  z: number;
  /** Where it comes from, and the tween that brings it in. */
  enter: Move;
  /** Where it goes, and the tween that takes it out. */
  exit: Move;
}

/**
 * Per style: how big a card is, how far apart the centers sit, how far a card
 * may wander off its cell, and how far it may be turned.
 *
 * `step` is an upper bound, not the answer — `safeOverlap` below cuts it down to
 * whatever the rotation actually allows. Writing the two independently is how
 * the first version of this file shipped a spread that left wedges of the old
 * screen showing between cards turned twenty degrees, on every viewport, which
 * is exactly what `coversViewport` is in the test file to catch.
 */
const STYLE_GRID = {
  // A dealt spread is nearly square: a few degrees is a hand, twenty is a mess.
  deal: { fraction: 0.3, step: 0.78, jitter: 0, maxRot: 4 },
  // Scattered, so it is packed tighter to survive the angles.
  spread: { fraction: 0.34, step: 0.62, jitter: 0.05, maxRot: 22 },
  cut: { fraction: 0.3, step: 0.78, jitter: 0, maxRot: 5 },
} as const;

/**
 * The brass rail that pushes a dealt spread off, as a physical object.
 *
 * It used to be decoration: the rail and the cards each ran their own easing
 * curve, the rail overtook the slow-starting cards and slid over them, and at
 * the end of the reveal the overlay was pulled with most of the pack still on
 * screen behind it — which read as the cards vanishing. Now there is ONE
 * position, `face`, and everything reads it: a card sits still until the rail's
 * face reaches its left edge and from then on moves exactly as the rail does, so
 * the pack gathers into a pile against the bar and goes off the far side with it.
 *
 * The width is here rather than only in CSS because the geometry needs it — the
 * rail has to travel until its own trailing edge clears the box.
 */
export interface Rail {
  width: number;
  /** Where the rail's leading face starts and ends, in px from the box's left. */
  from: number;
  to: number;
  /**
   * The face's position at `u` (0..1 of the reveal) AS DRAWN: straight between
   * `knots`, the way the keyframes play. Contact and push are measured against
   * this rather than the smooth curve it samples, so they agree with the screen.
   */
  face: (u: number) => number;
  /**
   * The instants (0..1 of the reveal) the bar is keyframed at, played linearly
   * between. Every pushed card is keyframed at these SAME instants from its
   * contact on, so bar and pile move in lock-step between keyframes too, rather
   * than two sets of straight segments approximating the curve differently.
   */
  knots: number[];
}

/** `.tx-rail`'s width: 7% of the box, never thinner than a thumb's worth of brass. */
export function railWidthFor(viewport: Viewport): number {
  return Math.max(26, Math.round(viewport.width * 0.07));
}

/**
 * The rail's motion profile: from rest, building speed like something heavy
 * being shoved, easing off a touch at the end once the table is clear. It does
 * not end at rest, so the last of the pile leaves with some pace on it.
 */
function railEase(u: number): number {
  return u * u * (2 - u);
}

/** When (0..1 of the reveal) the rail's face first reaches `x`, by bisection — it only ever moves forward. */
function contactTime(rail: Rail, x: number): number {
  if (rail.face(0) >= x) return 0;
  if (rail.face(1) <= x) return 1;
  let lo = 0;
  let hi = 1;
  for (let i = 0; i < 40; i++) {
    const mid = (lo + hi) / 2;
    if (rail.face(mid) < x) lo = mid;
    else hi = mid;
  }
  return lo;
}

/** Half the width of the upright box a card turned `rotDeg` occupies. */
function halfExtentX(width: number, height: number, rotDeg: number): number {
  const r = (rotDeg * Math.PI) / 180;
  return (width * Math.abs(Math.cos(r)) + height * Math.abs(Math.sin(r))) / 2;
}

/**
 * The rail for a pack. It starts just behind the leftmost card rather than at
 * the box edge: the grid overhangs the edges on purpose, and a rail starting
 * inside a card would be pushing it from within.
 */
function railFor(viewport: Viewport, leftmost: number): Rail {
  const width = railWidthFor(viewport);
  const from = Math.min(-8, leftmost - 2);
  // Far enough that the rail's own trailing edge has left the box, which puts
  // the face — and every card's left edge, all resting against it — past the
  // right edge too.
  const to = viewport.width + width + 8;
  const knots = Array.from({ length: RAIL_SAMPLES + 1 }, (_, i) => i / RAIL_SAMPLES);
  const at = knots.map((u) => from + (to - from) * railEase(u));
  const face = (u: number) => {
    const t = clamp(u, 0, 1) * RAIL_SAMPLES;
    const i = Math.min(RAIL_SAMPLES - 1, Math.floor(t));
    return at[i]! + (at[i + 1]! - at[i]!) * (t - i);
  };
  return { width, from, to, face, knots };
}

/** The rail a built deal pack is pushed by. `ScreenTransition` animates the bar from this. */
export function dealRail(cards: TransitionCard[], viewport: Viewport): Rail {
  const leftmost = cards.reduce((m, c) => Math.min(m, c.x - halfExtentX(c.width, c.height, c.rot)), 0);
  return railFor(viewport, leftmost);
}

/** How many segments the rail's travel is keyframed in. Linear between; the curve is gentle enough that 32 is smooth. */
const RAIL_SAMPLES = 32;

/**
 * A card is roughly the same size on every screen, which is what a card IS.
 *
 * Sizing purely off the short edge is how the first version got a desktop that
 * looked wrong: a 900-pixel-tall window put 360-pixel cards on the table, two
 * and a half times a phone's, and the effect stopped reading as a pack of cards
 * and started reading as a slideshow of enormous rectangles. The fraction now
 * only stretches the size between these two bounds, so a big window gets MORE
 * cards rather than bigger ones.
 *
 * `MAX_CARD_H` is also what `cardBackResolution` sizes the printed back for, so
 * raising it without raising that gives you a soft card.
 */
export const MIN_CARD_H = 104;
export const MAX_CARD_H = 190;
/**
 * A ceiling on the pack, scaled to the screen it is covering.
 *
 * It is NOT a fill-rate budget, and treating it as one is what made the cards
 * too big. This screen's cost is the one `pixi/renderQuality.ts` documents —
 * blended pixels, squared with the device pixel ratio — and the overdraw a pack
 * costs is set by the OVERLAP, not the count: forty big cards at a given overlap
 * shade exactly as much screen, and cost exactly as much layer memory, as a
 * hundred and sixty small ones. So a flat cap bought nothing and paid for it by
 * inflating every card on a large display.
 *
 * What the count does cost is one composited element each, so it is bounded by
 * AREA — a phone gets a pack it can afford, a desktop gets one that still looks
 * like cards. Past the ceiling they grow, which is the least-bad way to run out.
 */
function maxCardsFor(viewport: Viewport): number {
  return clamp(Math.round((viewport.width * viewport.height) / 5200), 80, 260);
}

/**
 * The backstop when the size is being driven by hand from `?tcard=`.
 *
 * A tuning flag that quietly gave you a different size than the one you typed
 * is worse than no flag — that is exactly the confusion the count budget caused
 * on its own — so the override skips the budget. This is only here so a stray
 * `?tcard=10` cannot ask the browser for ten thousand elements.
 */
const OVERRIDE_MAX_CARDS = 400;

/**
 * The largest fraction of a card that is guaranteed still covered once it may be
 * turned by `maxRotDeg`.
 *
 * A card at an angle no longer covers its own footprint: the axis-aligned box it
 * is certain to contain is the largest one whose corners still fall inside the
 * turned rectangle, which is `k` times the card where
 *
 *   k·(w·cos + h·sin) <= w   and   k·(w·sin + h·cos) <= h
 *
 * Both have to hold, so the smaller wins. At four degrees that is 0.91 of the
 * card and the grid barely notices; at twenty-two it is 0.69, which is why the
 * scatter needs half again as many cards for the same screen. Deriving it is the
 * point — a hand-tuned overlap is a number that is right until somebody changes
 * the angle next to it.
 */
function safeOverlap(cardW: number, cardH: number, maxRotDeg: number): number {
  const r = (maxRotDeg * Math.PI) / 180;
  const cos = Math.cos(r);
  const sin = Math.sin(r);
  return Math.min(cardW / (cardW * cos + cardH * sin), cardH / (cardW * sin + cardH * cos));
}

/** The seed only has to be stable within one run, so the layout can be rebuilt on a resize without reshuffling. */
/** The styles that deal a pack of cards, and so have a grid. */
type CardStyle = "deal" | "spread" | "cut";

export interface BuildOptions {
  back?: boolean;
  seed?: number;
  /** How many distinct faces there are to deal from. 0 means every card is face down. */
  handSize?: number;
  /** Roughly what fraction of the pack lands face down. */
  backShare?: number;
}

export function buildTransition(
  style: TransitionStyle,
  viewport: Viewport,
  plan: TransitionPlan,
  opts: BuildOptions = {},
): TransitionCard[] {
  // Neither of these is a pack of cards: the fade is one cross-fade, and the
  // slide moves the screens themselves. `ScreenTransition` drives both.
  if (style === "fade" || style === "slide") return [];
  const seed = opts.seed ?? 1;
  const grid = layOutGrid(style as CardStyle, viewport);
  const cards = (() => {
    switch (style) {
      case "deal":
        return dealt(grid, viewport, plan, seed);
      case "spread":
        return spread(grid, viewport, plan, seed);
      case "cut":
        return cut(grid, viewport, plan, !!opts.back);
    }
  })();
  return deal(cards, seed, opts.handSize ?? 0, opts.backShare ?? 0);
}

/**
 * Turns the pack over: which position shows which card, and which stay face
 * down.
 *
 * A separate pass rather than a line inside each style, because it is the same
 * question for all three and none of them has an opinion about it — and because
 * it wants its OWN random sequence. Folding it into the layouts would mean a
 * change to how one of them jitters a position silently re-shuffled the deck.
 */
function deal(cards: TransitionCard[], seed: number, handSize: number, backShare: number): TransitionCard[] {
  if (handSize <= 0) return cards.map((c) => ({ ...c, art: FACE_DOWN }));
  const rng = makeRng(seed + 101);
  // Cards are laid out in something close to neighbour order — grid order for the
  // cut, corner-outward for the deal — so a plain draw puts identical cards side
  // by side often enough to notice, and a pair of touching sevens is the one
  // thing that makes a spread look generated rather than dealt.
  //
  // Drawn from the hand MINUS the card just laid, rather than re-rolled until it
  // differs: one shift, no loop, and still uniform over everything else.
  let previous = FACE_DOWN;
  return cards.map((c) => {
    let art = FACE_DOWN;
    if (rng.next() >= backShare) {
      art = Math.floor(rng.next() * (previous === FACE_DOWN ? handSize : handSize - 1));
      if (previous !== FACE_DOWN && art >= previous) art += 1;
      art = Math.min(art, handSize - 1);
    }
    previous = art;
    return { ...c, art };
  });
}

interface Cell {
  x: number;
  y: number;
  col: number;
  row: number;
  cols: number;
  rows: number;
}

interface Grid {
  cells: Cell[];
  cardW: number;
  cardH: number;
  /** How far a card may wander off its cell, as a fraction of its own size. */
  jitter: number;
  /** How far a card may be turned, in degrees. */
  maxRot: number;
}

/**
 * A grid of card centers that covers the box, with the overlap the style's own
 * rotation and jitter allow.
 *
 * Centers run half a step OUTSIDE each edge as well as inside it, which is what
 * stops a turned card at the rim leaving a wedge of screen showing in the
 * corner. It costs one extra row and column and it is the difference between the
 * test passing and the player seeing the old screen through a gap.
 *
 * When that grid would run past `maxCardsFor`, the cards get BIGGER rather than
 * the grid getting shorter. Truncating the list is the obvious cheap fix and it
 * is precisely wrong: the cells it drops are the last ones, which is to say a
 * band along the bottom of the screen that then shows through.
 */
function layOutGrid(style: CardStyle, viewport: Viewport): Grid {
  const spec = STYLE_GRID[style];
  // A hand-set size means what it says; the automatic one is bounded.
  const budget = overriddenCardHeight === null ? maxCardsFor(viewport) : OVERRIDE_MAX_CARDS;
  let cardH = overriddenCardHeight ?? clamp(Math.min(viewport.width, viewport.height) * spec.fraction, MIN_CARD_H, MAX_CARD_H);
  let cardW = cardH * CARD_ASPECT;
  let cols = 0;
  let rows = 0;
  let stepX = 0;
  let stepY = 0;

  // Grow until the pack fits the budget. Bounded rather than `while (true)`: a
  // layout loop that cannot terminate is a frozen app, and 40 rounds of 8% is
  // already a card twenty times its starting size.
  for (let attempt = 0; attempt < 40; attempt++) {
    cardW = cardH * CARD_ASPECT;
    // The step has to leave room for BOTH the turn and the wander, or a card
    // that does each at once opens a seam its neighbours do not reach.
    const step = Math.min(spec.step, safeOverlap(cardW, cardH, spec.maxRot) - 2 * spec.jitter);
    stepX = cardW * step;
    stepY = cardH * step;
    cols = Math.ceil(viewport.width / stepX) + 1;
    rows = Math.ceil(viewport.height / stepY) + 1;
    if (cols * rows <= budget) break;
    cardH *= 1.08;
  }

  // Centered on the box, so the overhang is even on both sides.
  const originX = (viewport.width - (cols - 1) * stepX) / 2;
  const originY = (viewport.height - (rows - 1) * stepY) / 2;
  const cells: Cell[] = [];
  for (let row = 0; row < rows; row++) {
    for (let col = 0; col < cols; col++) {
      cells.push({ x: originX + col * stepX, y: originY + row * stepY, col, row, cols, rows });
    }
  }
  return { cells, cardW, cardH, jitter: spec.jitter, maxRot: spec.maxRot };
}

/**
 * Splits a phase's budget over a pack.
 *
 * Every card gets a delay somewhere in the first `spread` of the phase and then
 * however long is LEFT — so the last card to start is also the last to finish,
 * and they finish together on the phase's final frame. Sizing each card's
 * duration independently is the obvious alternative and it is wrong: the phase
 * would end while the slowest card was still travelling.
 */
function timed(order: number, count: number, phaseMs: number, spread: number, ease: string): { delayMs: number; durMs: number; ease: string } {
  const t = count <= 1 ? 0 : order / (count - 1);
  const delayMs = Math.round(phaseMs * spread * t);
  return { delayMs, durMs: phaseMs - delayMs, ease };
}

/* ---- Deal: four corners fill a tidy spread, a brass rail sweeps it off ---- */

/**
 * Each card is flicked in from its NEAREST corner, and the waves run outside-in
 * from that corner — which is what makes four dealers read as four dealers
 * rather than as noise arriving from four directions at once.
 */
function dealt(grid: Grid, viewport: Viewport, plan: TransitionPlan, seed: number): TransitionCard[] {
  const rng = makeRng(seed);
  const { cells, cardW, cardH } = grid;
  // Distance from the card's own corner decides when it is dealt: the corner's
  // first card lands first.
  const ranked = cells
    .map((c, i) => {
      const left = c.x < viewport.width / 2;
      const top = c.y < viewport.height / 2;
      const cornerX = left ? 0 : viewport.width;
      const cornerY = top ? 0 : viewport.height;
      return { c, i, left, top, d: Math.hypot(c.x - cornerX, c.y - cornerY) };
    })
    .sort((a, b) => a.d - b.d);

  const landed = ranked.map((r, order) => {
    const enter = timed(order, ranked.length, plan.coverMs, 0.62, "cubic-bezier(0.16, 0.9, 0.3, 1)");
    return {
      key: r.i,
      art: FACE_DOWN,
      x: r.c.x,
      y: r.c.y,
      width: cardW,
      height: cardH,
      rot: (rng.next() * 2 - 1) * grid.maxRot,
      z: order,
      enter: {
        // Flicked along its own diagonal, from outside the corner it belongs to.
        x: r.left ? -cardW : viewport.width + cardW,
        y: r.top ? -cardH : viewport.height + cardH,
        rot: (r.left ? -1 : 1) * (18 + rng.next() * 14),
        scale: 1,
        ...enter,
      },
    };
  });

  // Swept left to right by the rail. The whole pack has to be laid before the
  // rail can be placed, because it starts behind whichever card sits furthest left.
  const rail = dealRail(landed as TransitionCard[], viewport);
  return landed.map((c) => {
    // A card starts moving at the instant the rail's face reaches its left edge.
    // Resting against the face from then on, its offset from where it landed is
    // exactly how far the face has come since.
    const left = c.x - halfExtentX(c.width, c.height, c.rot);
    const contact = contactTime(rail, left);
    // Rounded DOWN, so the card is already moving when the face arrives rather
    // than a millisecond late with the bar a pixel into it; and never to the end
    // of the phase, because every card has to be seen to move.
    const delayMs = Math.min(Math.floor(plan.revealMs * contact), plan.revealMs - 1);
    const startU = delayMs / plan.revealMs;
    // Friction under a card that is being shoved turns it and walks it a little
    // off the line — which is most of what makes a pile look pushed rather than
    // carried. Both build up over the first card-and-a-half of travel and hold.
    const drift = (rng.next() * 2 - 1) * 9;
    const walk = (rng.next() * 2 - 1) * c.height * 0.06;
    const pushedAt = (u: number): PathPoint => {
      // Measured from where the face MEETS the card, not from where it was at
      // the rounded-down start: at full speed the bar covers a few pixels a
      // millisecond, and that rounding would otherwise open a visible gap.
      const d = Math.max(0, rail.face(u) - left);
      const k = Math.min(1, d / (c.width * 1.5));
      const rot = c.rot + drift * k;
      return {
        offset: (u - startU) / (1 - startU),
        // A card turning against the face pivots about its edge: moved on by
        // however much wider it has become, so no corner swings into the bar.
        x: c.x + d + (halfExtentX(c.width, c.height, rot) - halfExtentX(c.width, c.height, c.rot)),
        y: c.y + walk * k,
        rot,
      };
    };
    const path = [startU, ...rail.knots.filter((u) => u > startU)].map(pushedAt);
    const end = path[path.length - 1]!;
    return {
      ...c,
      exit: {
        x: end.x,
        y: end.y,
        rot: end.rot,
        scale: 1,
        delayMs,
        durMs: plan.revealMs - delayMs,
        ease: "linear",
        path,
      },
    };
  });
}

/* ---- Spread: cards fly in at every angle, then one gust takes them ------- */

function spread(grid: Grid, viewport: Viewport, plan: TransitionPlan, seed: number): TransitionCard[] {
  const rng = makeRng(seed + 7);
  const { cells, cardW, cardH } = grid;
  const centerX = viewport.width / 2;
  const centerY = viewport.height / 2;
  // Filled edges-first, so the last cards land in the middle where the eye is.
  const ranked = cells
    .map((c, i) => ({ c, i, d: -Math.hypot(c.x - centerX, c.y - centerY) }))
    .sort((a, b) => a.d - b.d);

  // One shared impulse, so the blow-off reads as weather rather than as every
  // card deciding for itself. Up and to the right, off the corner.
  const gust = -0.62;
  const gustDist = Math.hypot(viewport.width, viewport.height) * 1.25;

  return ranked.map((r, order) => {
    const enter = timed(order, ranked.length, plan.coverMs, 0.7, "cubic-bezier(0.12, 0.85, 0.28, 1.02)");
    const exit = timed(order, ranked.length, plan.revealMs, 0.34, "cubic-bezier(0.4, 0, 0.85, 0.5)");
    // Where it comes from: a random point outside the nearest edge.
    const angle = rng.next() * Math.PI * 2;
    const fly = Math.hypot(viewport.width, viewport.height) * 0.75;
    const speed = 0.8 + rng.next() * 0.45;
    return {
      key: r.i,
      art: FACE_DOWN,
      x: r.c.x + (rng.next() * 2 - 1) * cardW * grid.jitter,
      y: r.c.y + (rng.next() * 2 - 1) * cardH * grid.jitter,
      width: cardW,
      height: cardH,
      rot: (rng.next() * 2 - 1) * grid.maxRot,
      z: order,
      enter: {
        x: r.c.x + Math.cos(angle) * fly,
        y: r.c.y + Math.sin(angle) * fly,
        rot: (rng.next() * 2 - 1) * 180,
        scale: 1,
        ...enter,
      },
      exit: {
        x: r.c.x + Math.cos(gust) * gustDist * speed,
        y: r.c.y + Math.sin(gust) * gustDist * speed,
        rot: (rng.next() * 2 - 1) * 220,
        // Blown away from the viewer rather than merely off the side.
        scale: 0.82,
        ...exit,
      },
    };
  });
}

/* ---- Cut: one pack sweeps across, and its direction is the answer -------- */

/**
 * The minor one, and the reason it is worth half a second at all: it travels
 * the way the navigation did. Forward sweeps right to left, back sweeps left to
 * right, so a player who taps Back is told they went back before the new screen
 * has even arrived.
 *
 * A rigid pack rather than a scatter — the whole point is that it is over almost
 * before it started, and a hundred milliseconds of per-card randomness at this
 * length reads as a stutter.
 */
function cut(grid: Grid, viewport: Viewport, plan: TransitionPlan, back: boolean): TransitionCard[] {
  const { cells, cardW, cardH } = grid;
  const travel = viewport.width + cardW * 2;
  const dir = back ? 1 : -1;
  return cells.map((c, i) => {
    // A slight fan by column, so it is a pack of cards rather than a slab.
    const lead = back ? c.cols - 1 - c.col : c.col;
    const order = c.cols <= 1 ? 0 : lead / (c.cols - 1);
    const enter = timed(Math.round(order * 100), 101, plan.coverMs, 0.3, "cubic-bezier(0.2, 0.85, 0.3, 1)");
    const exit = timed(Math.round(order * 100), 101, plan.revealMs, 0.3, "cubic-bezier(0.4, 0, 0.9, 0.55)");
    return {
      key: i,
      art: FACE_DOWN,
      x: c.x,
      y: c.y,
      width: cardW,
      height: cardH,
      rot: clamp((c.col - (c.cols - 1) / 2) * 1.4, -grid.maxRot, grid.maxRot),
      z: back ? c.cols - c.col : c.col,
      // Comes in from the side it is travelling FROM and leaves out the far one,
      // so the pack crosses the screen in one continuous direction.
      enter: { x: c.x - dir * travel, y: c.y, rot: 0, scale: 1, ...enter },
      exit: { x: c.x + dir * travel, y: c.y, rot: 0, scale: 1, ...exit },
    };
  });
}

/* ---- The properties worth asserting ------------------------------------- */

/**
 * Is every point of the box under at least one landed card?
 *
 * Sampled rather than solved: the union of a few dozen rotated rectangles has no
 * cheap closed form, and a grid fine enough to catch a gap a player could see is
 * cheap enough to run in a test. A gap here is the old screen showing through at
 * the exact moment it is replaced.
 */
export function coversViewport(cards: TransitionCard[], viewport: Viewport, samples = 41): boolean {
  return uncoveredPoint(cards, viewport, samples) === null;
}

/** The first sampled point no card covers, for a failure message that says where. */
export function uncoveredPoint(
  cards: TransitionCard[],
  viewport: Viewport,
  samples = 41,
): { x: number; y: number } | null {
  for (let iy = 0; iy < samples; iy++) {
    for (let ix = 0; ix < samples; ix++) {
      const x = (viewport.width * ix) / (samples - 1);
      const y = (viewport.height * iy) / (samples - 1);
      if (!cards.some((c) => covers(c, x, y))) return { x, y };
    }
  }
  return null;
}

/** Point in a rotated rectangle: rotate the point into the card's own frame and compare. */
function covers(card: TransitionCard, x: number, y: number): boolean {
  const a = (-card.rot * Math.PI) / 180;
  const dx = x - card.x;
  const dy = y - card.y;
  const lx = dx * Math.cos(a) - dy * Math.sin(a);
  const ly = dx * Math.sin(a) + dy * Math.cos(a);
  return Math.abs(lx) <= card.width / 2 && Math.abs(ly) <= card.height / 2;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
